import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// webPush — abonnement réel au Web Push (notifications reçues MÊME
// application fermée).
//
// Le polling côté page ne fonctionne que lorsque l'app est ouverte : le
// téléphone gèle le JavaScript dès qu'on la met en veille. Le Web Push
// résout ce problème : le navigateur réveille le service worker à la
// réception d'un message poussé par le serveur, qui affiche alors la
// notification système.
//
// La clé publique VAPID est obtenue auprès de la fonction `send-push`
// (la clé privée ne quitte jamais le serveur).
// ─────────────────────────────────────────────────────────────

export function pushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window
  );
}

/** Convertit une clé VAPID base64url en tableau d'octets. */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output;
}

async function fetchVapidPublicKey(): Promise<string | null> {
  try {
    const { data, error } = await supabase.functions.invoke('send-push', {
      body: { action: 'get-key' },
    });
    if (error) return null;
    const key = (data as { publicKey?: string } | null)?.publicKey;
    return key ? String(key) : null;
  } catch {
    return null;
  }
}

/**
 * Crée (ou réutilise) l'abonnement Web Push du navigateur courant et
 * l'enregistre pour l'utilisateur. Idempotent et best-effort.
 */
export async function ensureWebPushSubscription(userId: number | string): Promise<boolean> {
  const uid = Number(userId);
  if (!Number.isFinite(uid) || uid <= 0) return false;
  if (!pushSupported()) return false;
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return false;

  try {
    const reg = await navigator.serviceWorker.ready;
    if (!reg?.pushManager) return false;

    const publicKey = await fetchVapidPublicKey();
    if (!publicKey) return false;

    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    }

    const json = sub.toJSON();
    const endpoint = json.endpoint || sub.endpoint;
    const p256dh = json.keys?.p256dh;
    const auth = json.keys?.auth;
    if (!endpoint || !p256dh || !auth) return false;

    // Remplace un éventuel abonnement existant pour ce même endpoint.
    await supabase.from('push_subscriptions').delete().eq('endpoint', endpoint);
    const { error } = await supabase.from('push_subscriptions').insert({
      user_id: uid,
      endpoint,
      p256dh,
      auth,
    });
    return !error;
  } catch {
    return false;
  }
}

/**
 * Demande au service worker une synchronisation périodique en arrière-plan
 * (Android / Chrome) : l'app installée est réveillée périodiquement même
 * fermée. Ignoré silencieusement si non supporté.
 */
export async function registerPeriodicSync(): Promise<void> {
  try {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
    const reg = await navigator.serviceWorker.ready;
    const ps = (reg as unknown as {
      periodicSync?: { register: (tag: string, opts?: { minInterval?: number }) => Promise<void> };
    }).periodicSync;
    if (!ps) return;
    try {
      await ps.register('zifek-refresh', { minInterval: 15 * 60 * 1000 });
    } catch {
      /* permission refusée ou navigateur non compatible */
    }
  } catch {
    /* best-effort */
  }
}